import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useOrganization } from '@/shared/hooks/api/useOrganization';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { SubscriptionService } from '@/features/Subscriptions/services/SubscriptionService';

const STATUS_COLOR: Record<string, string> = {
  active: 'text-emerald-600 dark:text-emerald-400',
  trial: 'text-amber-600 dark:text-amber-400',
  expired: 'text-red-500 dark:text-red-400',
  canceled: 'text-muted-foreground',
  past_due: 'text-red-500 dark:text-red-400',
  exhausted: 'text-red-500 dark:text-red-400',
};

export const useSidebarSubscription = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const { data: userProfile } = useOrganization();
  const { isSuperAdmin } = useIsSuperAdminRole();

  // A organização ativa vale para todos. O fallback para a organização home é só de
  // quem NÃO é superadmin: superadmin sem organização selecionada está em escopo de
  // plataforma, e ali não existe plano a exibir — cair na home dele mostraria a
  // assinatura de uma organização que não é a da tela.
  const orgId = selectedOrganization?.id ?? (isSuperAdmin ? undefined : userProfile?.organization_id);

  // O fetchApi injeta o header X-Organization-Id com a organização ativa para
  // qualquer usuário, então a resposta é sempre da organização ativa. O orgId
  // entra na queryKey para invalidar o cache ao trocar de organização — sem ele,
  // o plano da organização anterior continuaria sendo exibido.
  const { data: subscriptionData } = useQuery({
    queryKey: ['sidebar-subscription', orgId],
    queryFn: async () => {
      const response = await SubscriptionService.getMySubscription();
      return response.data;
    },
    enabled: isAuthenticated && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const statusLabel: Record<string, string> = {
    active: t('common.active', 'Ativo'),
    trial: 'Trial',
    expired: t('common.expired', 'Expirado'),
    canceled: t('common.canceled', 'Cancelado'),
    past_due: t('common.pastDue', 'Inadimplente'),
    exhausted: t('common.exhausted', 'Esgotado'),
  };

  return {
    planName: subscriptionData?.subscribed_plan?.name,
    subStatus: subscriptionData?.status,
    statusLabel,
    statusColor: STATUS_COLOR,
  };
};
